import React, { useEffect, useMemo, useState, useCallback } from "react";
import { supabase } from "../../lib/supabase";
import {
  Plus, Trash2, Calendar, X, Receipt, ChevronLeft, ChevronRight,
  Coffee, ShoppingBag, Car, Home, Heart, MoreHorizontal,
  TrendingDown, Download, SlidersHorizontal, RefreshCcw, ArrowUpRight, ArrowDownRight,
  Birdhouse
} from "lucide-react";
import toast from "react-hot-toast";
import { format } from "date-fns";

const CATEGORIES = [
  { id: "Food", icon: Coffee, color: "bg-orange-500 text-orange-600", light: "bg-orange-50 border-orange-100" },
  { id: "Shopping", icon: ShoppingBag, color: "bg-blue-500 text-blue-600", light: "bg-blue-50 border-blue-100" },
  { id: "Travel", icon: Car, color: "bg-purple-500 text-purple-600", light: "bg-purple-50 border-purple-100" },
  { id: "Bills", icon: Birdhouse, color: "bg-emerald-500 text-emerald-600", light: "bg-emerald-50 border-emerald-100" },
  { id: "Health", icon: Heart, color: "bg-rose-500 text-rose-600", light: "bg-rose-50 border-rose-100" },
  { id: "Ghar", icon: Home, color: "bg-orange-500 text-orange-600", light: "bg-orange-50 border-orange-100" },
  { id: "Other", icon: MoreHorizontal, color: "bg-slate-500 text-slate-600", light: "bg-slate-50 border-slate-100" },
];

export default function PersonalExpenses() {
  const todayStr = format(new Date(), "yyyy-MM-dd");
  
  // Operational Filtering States
  const [filterMode, setFilterMode] = useState("MONTH"); // SINGLE, MONTH, RANGE
  const [selectedDate, setSelectedDate] = useState(todayStr);
  const [selectedMonth, setSelectedMonth] = useState(format(new Date(), "yyyy-MM"));
  const [fromDate, setFromDate] = useState(todayStr);
  const [toDate, setToDate] = useState(todayStr);

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ totalIncome: 0, totalExpense: 0, netSavings: 0 });

  // Add Form UI State
  const [showAddForm, setShowAddForm] = useState(false);
  const [form, setForm] = useState({
    type: "expense", // 'income' or 'expense'
    category: "Food",
    title: "",
    amount: "",
    notes: "",
    date: todayStr,
    time: format(new Date(), "HH:mm"),
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      let query = supabase.from("personal_expenses").select("*");

      // Apply Filter Modes
      if (filterMode === "SINGLE") {
        query = query.eq("date", selectedDate);
      } else if (filterMode === "MONTH") {
        const start = `${selectedMonth}-01`;
        const endDateObj = new Date(selectedMonth + "-01");
        endDateObj.setMonth(endDateObj.getMonth() + 1);
        const end = format(endDateObj, "yyyy-MM-dd");
        query = query.gte("date", start).lt("date", end);
      } else if (filterMode === "RANGE") {
        if (fromDate && toDate) {
          query = query.gte("date", fromDate).lte("date", toDate);
        }
      }

      const { data, error } = await query.order("created_at", { ascending: false });
      if (error) throw error;

      const rawEntries = data || [];
      setEntries(rawEntries);

      // Aggregate ledger variables
      const aggregated = rawEntries.reduce((acc, item) => {
        const amt = Number(item.amount || 0);
        if (item.type === "income") {
          acc.income += amt;
        } else {
          acc.expense += amt;
        }
        return acc;
      }, { income: 0, expense: 0 });

      setStats({
        totalIncome: aggregated.income,
        totalExpense: aggregated.expense,
        netSavings: aggregated.income - aggregated.expense
      });

    } catch (err) {
      console.error("Ledger engine error:", err);
      toast.error("Failed to sync transaction records");
    } finally {
      setLoading(false);
    }
  }, [filterMode, selectedDate, selectedMonth, fromDate, toDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDateShift = (days) => {
    const current = new Date(selectedDate);
    current.setDate(current.getDate() + days);
    setSelectedDate(format(current, "yyyy-MM-dd"));
  };

  const handleAddExpense = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.amount) {
      return toast.error("Please supply a valid title and quantity value");
    }

    try {
      const timestamp = `${form.date}T${form.time || "00:00"}:00`;
      const { error } = await supabase.from("personal_expenses").insert([{
        type: form.type,
        title: form.title.trim(),
        category: form.type === "income" ? "Salary" : form.category,
        amount: Number(form.amount),
        notes: form.notes.trim() || null,
        date: form.date,
        created_at: timestamp
      }]);

      if (error) throw error;

      toast.success("Transaction entry stored successfully");
      setShowAddForm(false);
      setForm({
        type: "expense",
        category: "Food",
        title: "",
        amount: "",
        notes: "",
        date: todayStr,
        time: format(new Date(), "HH:mm"),
      });
      fetchData();
    } catch (err) {
      toast.error(err.message || "Failed to commit record");
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm("Permanently erase this ledger transaction record?")) return;
    try {
      const { error } = await supabase.from("personal_expenses").delete().eq("id", entryId);
      if (error) throw error;
      toast.success("Record deleted");
      fetchData();
    } catch (err) {
      toast.error("Deletion lifecycle abort anomaly");
    }
  };

  // Memoized categorization totals
  const categoryTotals = useMemo(() => {
    return entries.reduce((acc, curr) => {
      if (curr.type === "expense") {
        acc[curr.category] = (acc[curr.category] || 0) + Number(curr.amount || 0);
      }
      return acc;
    }, {});
  }, [entries]);

  return (
    <div className="min-h-screen bg-[#F1F5F9] pb-24">
      
      {/* 🔷 CONTROL BAR STICKY HEADER */}
      <div className="bg-white border-b border-slate-200 px-4 py-5 lg:px-8 sticky top-0 z-40 shadow-xs">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row justify-between md:items-center gap-4">
          <div>
            <div className="flex items-center gap-2 text-[#0054A6]">
              <Receipt size={22} />
              <h1 className="text-xl font-black tracking-tight text-slate-900 uppercase">Cash & Expense Ledger</h1>
            </div>
            <p className="text-xs text-slate-400 font-bold uppercase tracking-wider mt-0.5">Personal Liquidity Tracking Dashboard</p>
          </div>

          {/* DYNAMIC FILTER MECHANICS CHANNELS */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="bg-slate-100 p-1 rounded-xl flex border border-slate-200">
              {["SINGLE", "MONTH", "RANGE"].map((mode) => (
                <button
                  key={mode}
                  onClick={() => setFilterMode(mode)}
                  className={`px-3 py-1.5 rounded-lg text-[10px] font-black uppercase transition-all ${
                    filterMode === mode ? "bg-white text-[#0054A6] shadow-xs" : "text-slate-400 hover:text-slate-600"
                  }`}
                >
                  {mode === "SINGLE" ? "Day" : mode}
                </button>
              ))}
            </div>

            {/* CONDITIONAL FILTER INPUT CONTROLS */}
            <div className="bg-white p-1 rounded-xl border border-slate-200 flex items-center shadow-xs">
              {filterMode === "SINGLE" && (
                <div className="flex items-center gap-1">
                  <button onClick={() => handleDateShift(-1)} className="p-1 text-slate-400 hover:text-slate-900"><ChevronLeft size={16}/></button>
                  <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} className="text-xs font-bold text-slate-700 outline-none p-1 bg-transparent border-none" />
                  <button onClick={() => handleDateShift(1)} className="p-1 text-slate-400 hover:text-slate-900"><ChevronRight size={16}/></button>
                </div>
              )}
              {filterMode === "MONTH" && (
                <input type="month" value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} className="text-xs font-bold text-slate-700 outline-none p-1 bg-transparent border-none px-2" />
              )}
              {filterMode === "RANGE" && (
                <div className="flex items-center gap-1 text-xs font-bold text-slate-600 px-1">
                  <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="bg-transparent border-none text-slate-700 outline-none" />
                  <span className="text-slate-300">to</span>
                  <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="bg-transparent border-none text-slate-700 outline-none" />
                </div>
              )}
            </div>

            <button onClick={fetchData} className="p-2.5 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-xl transition-colors border">
              <RefreshCcw size={15} />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto p-4 md:p-6 space-y-6 mt-4">

        {/* 🔷 BALANCE SHEET STATS CARDS */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white p-5 rounded-3xl border shadow-xs">
            <p className="text-[10px] font-black uppercase text-slate-400 tracking-wider mb-1">Total Period Income</p>
            <h3 className="text-2xl font-black text-emerald-600">₹{stats.totalIncome.toLocaleString("en-IN")}</h3>
          </div>
          <div className="bg-white p-5 rounded-3xl border shadow-xs">
            <p className="text-[10px] font-black uppercase text-slate-400 tracking-wider mb-1">Total Period Expenses</p>
            <h3 className="text-2xl font-black text-rose-500">₹{stats.totalExpense.toLocaleString("en-IN")}</h3>
          </div>
          <div className="bg-white p-5 rounded-3xl border shadow-xs">
            <p className="text-[10px] font-black uppercase text-slate-400 tracking-wider mb-1">Net Balance Difference</p>
            <h3 className={`text-2xl font-black ${stats.netSavings >= 0 ? "text-blue-600" : "text-orange-500"}`}>
              ₹{stats.netSavings.toLocaleString("en-IN")}
            </h3>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
          
          {/* 🔷 LEFT COLUMN: RUNNING TRANSACTIONS PANEL */}
          <div className="lg:col-span-8 space-y-4">
            <div className="flex justify-between items-center px-2">
              <h2 className="font-black text-slate-800 text-sm uppercase tracking-widest">Running Statements ({entries.length})</h2>
              <span className="text-[10px] bg-slate-200 text-slate-600 font-bold px-2.5 py-1 rounded-full uppercase">
                {filterMode === "SINGLE" ? "Day List" : filterMode === "MONTH" ? "Monthly Sheet" : "Custom Window"}
              </span>
            </div>

            {loading ? (
              <div className="text-center py-20 font-black tracking-widest text-[#0054A6] animate-pulse text-xs uppercase">Syncing account balance data streams...</div>
            ) : entries.length === 0 ? (
              <div className="bg-white rounded-[2rem] p-12 text-center border shadow-xs">
                <Receipt className="mx-auto text-slate-200 mb-3" size={36} />
                <h4 className="font-black text-slate-800 uppercase text-xs">No entries flagged</h4>
                <p className="text-xs text-slate-400 mt-1">There are no financial logs tracked for the current filter context parameters.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {entries.map((item) => {
                  const categoryMeta = CATEGORIES.find(c => c.id === item.category) || CATEGORIES[5];
                  const isIncome = item.type === "income";

                  return (
                    <div key={item.id} className="bg-white p-4 rounded-[2rem] border border-slate-200 shadow-xs flex justify-between items-center transition-all hover:shadow-md">
                      <div className="flex items-center gap-4 min-w-0">
                        <div className={`p-3 rounded-2xl border ${isIncome ? 'bg-emerald-50 border-emerald-100 text-emerald-600' : `${categoryMeta.light} ${categoryMeta.color.split(' ')[1]}`}`}>
                          {isIncome ? <ArrowUpRight size={18}/> : <categoryMeta.icon size={18} />}
                        </div>
                        <div className="min-w-0">
                          <p className="font-black text-slate-800 text-sm truncate">{item.title}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400">
                              {format(new Date(item.date), "dd MMM, yyyy")}
                            </span>
                            <span className={`text-[8px] font-black uppercase px-2 py-0.5 rounded-md ${isIncome ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-600'}`}>
                              {isIncome ? "Income" : item.category}
                            </span>
                          </div>
                          {item.notes && <p className="text-[11px] text-slate-400 font-medium italic mt-1 line-clamp-1">"{item.notes}"</p>}
                        </div>
                      </div>

                      <div className="flex items-center gap-4 shrink-0 pl-4">
                        <p className={`font-black text-base ${isIncome ? "text-emerald-600" : "text-slate-900"}`}>
                          {isIncome ? "+" : "-"}₹{Number(item.amount || 0).toLocaleString("en-IN")}
                        </p>
                        <button onClick={() => handleDelete(item.id)} className="p-1 text-slate-300 hover:text-rose-500 transition-colors">
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* 🔷 RIGHT COLUMN: CATEGORICAL DISPERSAL PANELS */}
          <div className="lg:col-span-4 bg-white p-6 rounded-[2.5rem] border shadow-xs space-y-6">
            <div>
              <h3 className="font-black text-slate-800 text-xs uppercase tracking-widest">Categorical Spend Mix</h3>
              <p className="text-[10px] text-slate-400 font-bold uppercase mt-0.5">Direct expense distribution analysis</p>
            </div>

            <div className="space-y-3">
              {Object.keys(categoryTotals).length === 0 ? (
                <div className="text-center py-6 text-xs font-bold text-slate-400 border border-dashed rounded-2xl bg-slate-50 uppercase">
                  No active expenses logged
                </div>
              ) : (
                Object.entries(categoryTotals).map(([cat, total]) => {
                  const meta = CATEGORIES.find(c => c.id === cat) || CATEGORIES[5];
                  const percentage = ((total / (stats.totalExpense || 1)) * 100).toFixed(0);

                  return (
                    <div key={cat} className="p-3.5 bg-slate-50 border border-slate-200/60 rounded-2xl space-y-2">
                      <div className="flex justify-between items-center text-xs">
                        <span className="flex items-center gap-2 font-black text-slate-700 uppercase text-[10px]">
                          <div className={`w-2 h-2 rounded-full ${meta.color.split(' ')[0]}`} />
                          {cat}
                        </span>
                        <span className="font-black text-slate-900">₹{total.toLocaleString("en-IN")} <span className="text-[10px] text-slate-400 font-normal">({percentage}%)</span></span>
                      </div>
                      <div className="w-full bg-slate-200/70 h-1.5 rounded-full overflow-hidden">
                        <div className={`h-full ${meta.color.split(' ')[0]}`} style={{ width: `${percentage}%` }} />
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

        </div>

      </div>

      {/* 🔷 INTERACTIVE FLOATING CREATE TRIGGER BUTTON */}
      <button
        onClick={() => setShowAddForm(true)}
        className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-[#0054A6] hover:bg-blue-700 text-white px-8 py-4 rounded-full shadow-xl shadow-blue-200 font-black text-xs uppercase tracking-widest flex items-center gap-2.5 transition-all hover:-translate-y-0.5 z-50"
      >
        <Plus size={16} strokeWidth={3} /> Add Transaction
      </button>

      {/* 🔷 ADD TRANSACTION DRAWER MODAL OVERLAY */}
      {showAddForm && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-xs z-[100] flex items-center justify-center p-4 animate-in fade-in duration-150">
          <div className="bg-white w-full max-w-md rounded-[2.5rem] p-6 sm:p-8 shadow-2xl animate-in zoom-in-95 duration-200 max-h-[90vh] overflow-y-auto custom-scrollbar">
            
            <div className="flex items-center justify-between mb-6 border-b pb-3 border-slate-100">
              <h2 className="text-xl font-black text-slate-900 uppercase tracking-tight">Log Transaction</h2>
              <button onClick={() => setShowAddForm(false)} className="bg-slate-100 hover:bg-slate-200 p-1.5 rounded-full transition-colors">
                <X size={16} />
              </button>
            </div>

            <form onSubmit={handleAddExpense} className="space-y-5">
              
              {/* TRANSACTION TYPE SELECTOR */}
              <div className="bg-slate-100 p-1 rounded-xl flex border">
                <button
                  type="button"
                  onClick={() => setForm({ ...form, type: "expense" })}
                  className={`flex-1 py-2 rounded-lg text-[10px] font-black uppercase transition-all ${form.type === "expense" ? "bg-white text-rose-600 shadow-xs" : "text-slate-400"}`}
                >
                  Expense Outflow
                </button>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, type: "income" })}
                  className={`flex-1 py-2 rounded-lg text-[10px] font-black uppercase transition-all ${form.type === "income" ? "bg-white text-emerald-600 shadow-xs" : "text-slate-400"}`}
                >
                  Income Inflow
                </button>
              </div>

              {/* DYNAMIC CATEGORY BUTTON GRID MATRIX (Hidden if Income) */}
              {form.type === "expense" && (
                <div className="space-y-1.5">
                  <label className="text-[9px] font-black uppercase tracking-wider text-slate-400 ml-1">Select Category</label>
                  <div className="grid grid-cols-3 gap-2">
                    {CATEGORIES.map((cat) => (
                      <button
                        key={cat.id}
                        type="button"
                        onClick={() => setForm({ ...form, category: cat.id })}
                        className={`p-2.5 rounded-xl border-2 transition-all flex flex-col items-center gap-1 ${
                          form.category === cat.id ? "border-blue-500 bg-blue-50/60 text-blue-700" : "border-transparent bg-slate-50 text-slate-600 hover:bg-slate-100"
                        }`}
                      >
                        <cat.icon size={16} />
                        <span className="text-[10px] font-bold tracking-tight">{cat.id}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* DATE & TIME SCHEDULING ROW */}
              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-1">
                  <label className="text-[9px] font-black uppercase tracking-wider text-slate-400 ml-1">Date</label>
                  <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="bg-slate-50 border border-slate-200 p-3 rounded-xl outline-none font-bold text-xs" />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-[9px] font-black uppercase tracking-wider text-slate-400 ml-1">Time</label>
                  <input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="bg-slate-50 border border-slate-200 p-3 rounded-xl outline-none font-bold text-xs" />
                </div>
              </div>

              {/* TEXTUAL INPUT TARGET DETAILS FIELDS */}
              <div className="flex flex-col gap-1">
                <label className="text-[9px] font-black uppercase tracking-wider text-slate-400 ml-1">Transaction Title / Payee</label>
                <input placeholder="E.g., Office office stationeries / Client retainer payment" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full bg-slate-50 border border-slate-200 p-3.5 rounded-xl outline-none font-bold text-sm text-slate-800 focus:border-blue-500" />
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-[9px] font-black uppercase tracking-wider text-slate-400 ml-1">Amount (₹)</label>
                <input type="number" placeholder="0.00" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="w-full bg-slate-50 border border-slate-200 p-3.5 rounded-xl outline-none text-xl font-black text-slate-900 focus:border-blue-500" />
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-[9px] font-black uppercase tracking-wider text-slate-400 ml-1">Add Notes / Reference Context</label>
                <textarea placeholder="Write payment tracking details..." value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full bg-slate-50 border border-slate-200 p-3.5 rounded-xl outline-none h-16 text-xs font-medium resize-none focus:border-blue-500" />
              </div>

              <button type="submit" className="w-full bg-[#0054A6] hover:bg-blue-700 text-white py-4 rounded-xl font-black text-xs uppercase tracking-widest shadow-md transition-colors mt-2">
                Commit Transaction Record
              </button>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}